import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Layers } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const ProtectiveCoatings = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["services/protective-coatings"]?.title} description={seoData["services/protective-coatings"]?.description} path="/services/protective-coatings" keywords={seoData["services/protective-coatings"]?.keywords || []} />

      <PageHero
        badge="Core Services"
        title="Protective Coatings & Corrosion Prevention"
        description="Certified protective coatings contractor serving California. Industrial coating services including epoxy, polyurethane, polyurea, and ceramic-filled coating systems. Corrosion protection coatings for tanks, pipes, steel structures, and concrete. High performance, immersion grade, and chemical-resistant coating systems."
        backgroundKey="protective-coatings"
        buttonText="Get In Touch"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Problems We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Atmospheric corrosion on exposed steel structures and tanks',
              'Immersion corrosion in tanks, pipes, and submerged structures',
              'Chemical attack from acids, caustics, and solvents',
              'Erosion and abrasion damage on material handling equipment',
              'UV degradation and weathering on exterior coatings',
              'Coating failure from improper surface preparation or system selection'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-emerald-50 rounded-lg">
                <Shield className="text-emerald-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-emerald-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Layers className="text-emerald-600 mr-3 flex-shrink-0" size={22} />
                  Epoxy Coating Systems
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">High-build epoxy coatings for steel and concrete substrates. Immersion-grade epoxy tank linings, chemical-resistant floor coatings, and structural steel protection. Systems from 6-40 mils DFT tailored to your service environment.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Layers className="text-emerald-600 mr-3 flex-shrink-0" size={22} />
                  Polyurethane & Polyurea Systems
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Fast-cure polyurethane and polyurea coatings for rapid return to service. Secondary containment linings, waterproofing membranes, and abrasion-resistant coatings. Spray-applied systems from 20-250 mils.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Layers className="text-emerald-600 mr-3 flex-shrink-0" size={22} />
                  Ceramic-Filled Coating Systems
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Belzona ceramic-filled coatings for erosion, abrasion, and chemical resistance. Chute linings, pump protection, and pipe coatings using Belzona 1321, 1391, and 1812 systems for extreme wear environments.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Layers className="text-emerald-600 mr-3 flex-shrink-0" size={22} />
                  High-Temperature Coatings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Specialized coatings for equipment and structures operating at elevated temperatures up to 1200°F. Thermal barrier coatings, exhaust system protection, and heat-resistant primers for hot service.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS</h2>
              <div className="space-y-3">
                {['Full range of coating systems — epoxy, polyurethane, polyurea, ceramic, and specialty',
                  'Immersion-grade coatings for tanks, pipes, and submerged service',
                  'Chemical resistance testing and compatibility verification for your process',
                  'AMPP/NACE certified coating applicators',
                  'Complete turnkey service — assessment, surface prep, application, and QA/QC',
                  'Full QA/QC documentation: DFT readings, adhesion testing, holiday detection',
                  'Manufacturer-backed warranties with extended coverage options',
                  'On-site mobile blasting and surface preparation'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-900 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-xl font-bold mb-4">Belzona Products We Use</h3>
              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 5811 (Immersion Grade)</p>
                  <p className="text-xs text-slate-500 mt-1">Tank lining and immersion service coatings</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 5831 (ST-Barrier)</p>
                  <p className="text-xs text-slate-500 mt-1">Surface-tolerant protective barrier coating</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1321 (Ceramic S-Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">Erosion and corrosion-resistant repair</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 5891 (HT-Barrier)</p>
                  <p className="text-xs text-slate-500 mt-1">High-temperature barrier coating</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Stop Corrosion Before It Stops You</h2>
          <p className="text-blue-100 mb-8">Contact our coating specialists for a free corrosion assessment and coating recommendation. We'll match the right system to your environment for maximum protection and service life.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProtectiveCoatings;
